/*
 * NYTRON — Gallery Color Visualizer (v4 — persistent per-slide)
 * Recolors the bike's lights to any color under the product gallery.
 *
 * v4 change: the recolor is painted onto a canvas INSIDE EACH image slide (not one canvas over the
 * gallery). Each slide carries its own colored layer, so the chosen color stays baked on every photo
 * and moves WITH the slide during a swipe — no revert to the original while scrolling. Painted once per
 * colour pick (the current slide live, the rest a beat later), then it just rides along with the carousel.
 *
 * - Direct hue-set on lit pixels only; background stays natural. White-hot LED core preserved.
 * - White is the far-left of the slider (negative value = white).
 * - Round knob fills with the chosen colour (--nt-knob).
 * - Shows only on chosen photos (explicit image-number list) or auto-detected lit photos.
 * - Per-image object-fit + container-ratio canvas so the overlay always aligns (no stretch).
 */
(function () {
  'use strict';

  function ready(fn) { if (document.readyState !== 'loading') fn(); else document.addEventListener('DOMContentLoaded', fn); }
  function clamp01(x) { return x < 0 ? 0 : x > 1 ? 1 : x; }
  function smooth(e0, e1, x) { var t = clamp01((x - e0) / (e1 - e0)); return t * t * (3 - 2 * t); }
  function hue2(p, q, t) { if (t < 0) t += 1; if (t > 1) t -= 1; if (t < 1 / 6) return p + (q - p) * 6 * t; if (t < 1 / 2) return q; if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6; return p; }
  function hslToRgb(h, s, l) { if (s === 0) { var v = l * 255; return [v, v, v]; } var q = l < 0.5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q; return [hue2(p, q, h + 1 / 3) * 255, hue2(p, q, h) * 255, hue2(p, q, h - 1 / 3) * 255]; }

  function build(ui) {
    var gallery = ui.closest('.nt-bbp__gallery'); if (!gallery) return;
    var main = gallery.querySelector('.nt-bbp__main');
    var track = gallery.querySelector('.nt-bbp__track');
    if (!main || !track) return;

    var RES = (window.innerWidth && window.innerWidth < 700) ? 520 : 640;
    var SAT_LO = 0.16, SAT_HI = 0.34, COVER_MIN = 0.012;
    var intensity = parseFloat(ui.getAttribute('data-intensity')); if (isNaN(intensity)) intensity = 70; intensity = intensity / 100;

    // explicit image list (1-based positions) -> allowed 0-based indices; empty = auto-detect
    var only = (ui.getAttribute('data-only') || '').trim();
    var allowed = null;
    if (only) { allowed = {}; var any = false; only.split(',').forEach(function (x) { var n = parseInt(x, 10); if (!isNaN(n)) { allowed[n - 1] = true; any = true; } }); if (!any) allowed = null; }

    var swatches = [].slice.call(ui.querySelectorAll('[data-bbp-swatch]'));

    var recCache = {};      // idx -> precomputed recolor mask (shared by a slide and its clones)
    var outBuf = null;      // reusable ImageData for painting
    var state = { hue: parseFloat(ui.getAttribute('data-hue')) || 210, sat: 1, white: false, engaged: false };

    function sig() { return state.white ? 'W' : ('H' + Math.round(state.hue) + 'S' + Math.round(state.sat * 100)); }
    function slideIdx(sl) { var v = sl.getAttribute('data-bbp-slide'); return v == null ? -1 : parseInt(v, 10); }
    function slideImg(sl) { if (sl.querySelector('video, iframe')) return null; return sl.querySelector('img'); }
    function slideCanvas(sl) { return sl.querySelector('[data-bbp-recolor]'); }
    function allSlides() { return track.querySelectorAll('.nt-bbp__slide'); }
    function currentSlide() { return track.querySelector('.nt-bbp__slide.is-current') || track.querySelector('.nt-bbp__slide.is-active') || track.querySelector('.nt-bbp__slide'); }

    function canvasDims() {
      var rect = main.getBoundingClientRect();
      var arr = (rect.width > 0 && rect.height > 0) ? (rect.height / rect.width) : 1;
      return { W: RES, H: Math.max(1, Math.round(RES * arr)) };
    }

    // Build (once per image index) the lit-pixel mask, drawn at the gallery aspect ratio with the
    // image's own object-fit so the recolor lines up with what's displayed.
    function precompute(idx, img, fit) {
      if (recCache[idx]) return recCache[idx];
      if (!img || !img.complete || !img.naturalWidth) return null;
      var dims = canvasDims(), W = dims.W, H = dims.H;
      var cv = document.createElement('canvas'); cv.width = W; cv.height = H;
      var cx = cv.getContext('2d', { willReadFrequently: true }); if (!cx) return null;
      var imgFit = (fit || 'cover').toLowerCase();
      var iw = img.naturalWidth, ih = img.naturalHeight;
      var sc = imgFit === 'contain' ? Math.min(W / iw, H / ih) : Math.max(W / iw, H / ih);
      var dw = iw * sc, dh = ih * sc;
      cx.clearRect(0, 0, W, H); cx.drawImage(img, (W - dw) / 2, (H - dh) / 2, dw, dh);
      var base; try { base = cx.getImageData(0, 0, W, H); } catch (e) { return null; }
      var N = W * H, d = base.data, idxA = [], csA = [], clA = [], cwA = [], colored = 0, sumL = 0;
      for (var i = 0, p = 0; i < N; i++, p += 4) {
        var r = d[p] / 255, g = d[p + 1] / 255, b = d[p + 2] / 255;
        var mx = Math.max(r, g, b), mn = Math.min(r, g, b), l = (mx + mn) / 2, sat = 0; sumL += l;
        if (mx !== mn) { var dd = mx - mn; sat = l > 0.5 ? dd / (2 - mx - mn) : dd / (mx + mn); }
        var w = clamp01((sat - SAT_LO) / (SAT_HI - SAT_LO));
        w *= smooth(0.06, 0.16, l);
        w *= (1 - smooth(0.90, 0.99, l) * 0.55);
        if (w > 0.01) { idxA.push(p); csA.push(sat); clA.push(l); cwA.push(w); colored++; }
      }
      var rec = { base: base, idx: new Int32Array(idxA), cs: new Float32Array(csA), cl: new Float32Array(clA), cw: new Float32Array(cwA), cover: colored / N, avgL: sumL / N, W: W, H: H };
      recCache[idx] = rec; return rec;
    }

    function recolorable(idx, rec) {
      if (allowed) return !!allowed[idx];                 // explicit list: no pixels needed
      return rec ? (rec.cover >= COVER_MIN && rec.avgL < 0.42) : false;
    }

    function paintInto(canvas, rec) {
      var W = rec.W, H = rec.H;
      if (canvas.width !== W) canvas.width = W;
      if (canvas.height !== H) canvas.height = H;
      var cx = canvas.getContext('2d', { willReadFrequently: true }); if (!cx) return;
      if (!outBuf || outBuf.width !== W || outBuf.height !== H) outBuf = cx.createImageData(W, H);
      var d = rec.base.data, o = outBuf.data; o.set(d);
      var idx = rec.idx, cs = rec.cs, cl = rec.cl, cw = rec.cw, n = idx.length, h = state.hue / 360, white = state.white;
      for (var k = 0; k < n; k++) {
        var p = idx[k], wgt = cw[k], L = cl[k], c;
        if (white) { var wl = smooth(0.22, 0.78, L); var ll = clamp01(0.16 + wl * 0.98); c = [255 * ll * 0.96, 255 * ll * 0.985, 255 * ll]; }
        else { var core = smooth(0.55, 0.92, L); var satMul = 0.4 + 0.6 * state.sat; var sV = clamp01(cs[k] * intensity * satMul * (1 - core * 0.9)); c = hslToRgb(h, sV, L); }
        o[p] = d[p] + (c[0] - d[p]) * wgt; o[p + 1] = d[p + 1] + (c[1] - d[p + 1]) * wgt; o[p + 2] = d[p + 2] + (c[2] - d[p + 2]) * wgt;
      }
      cx.putImageData(outBuf, 0, 0);
      canvas.style.display = 'block';
      canvas.__ntSig = sig();
    }

    // Ensure one slide's canvas carries the current colour (or hide it if not applicable / not engaged).
    // If the slide's own <img> hasn't lazy-loaded yet, we PRELOAD the same src so we can paint the colour
    // onto it immediately — that way every photo is already coloured before it's scrolled into view.
    function ensureSlide(sl, force) {
      var img = slideImg(sl), canvas = slideCanvas(sl);
      if (!canvas) return;
      if (!img) { canvas.style.display = 'none'; return; }
      var idx = slideIdx(sl);
      if (allowed && !allowed[idx]) { canvas.style.display = 'none'; return; }
      var fit = (window.getComputedStyle ? getComputedStyle(img).objectFit : 'cover');
      function apply(pix) {
        var rec = precompute(idx, pix, fit);
        if (!state.engaged || !recolorable(idx, rec)) { canvas.style.display = 'none'; return; }
        if (!force && canvas.__ntSig === sig() && canvas.style.display === 'block') return;
        paintInto(canvas, rec);
      }
      if (recCache[idx]) { apply(null); return; }               // mask already built (shared with clones)
      if (img.complete && img.naturalWidth) { apply(img); return; }
      // Not loaded yet (lazy, offscreen): nudge it to fetch now so it colours before it's scrolled to.
      try { img.loading = 'eager'; } catch (e) {}
      if (!img.__ntHooked) { img.__ntHooked = 1; img.addEventListener('load', function () { apply(img); }); }
    }

    // Paint the centred slide first (live feedback), then every other slide, so all photos carry the
    // colour and it persists as the carousel scrolls. Loaded images paint now; not-yet-loaded ones are
    // nudged to load and paint on their load event.
    function refresh() {
      if (!state.engaged) return;
      var cur = currentSlide(); if (cur) ensureSlide(cur, false);
      var slides = allSlides();
      for (var i = 0; i < slides.length; i++) { if (slides[i] !== cur) ensureSlide(slides[i], false); }
    }

    // Show the slider control only when the centred image is recolourable.
    function updateControlVisibility() {
      var sl = currentSlide(); if (!sl) { ui.style.display = 'none'; return; }
      var img = slideImg(sl); if (!img) { ui.style.display = 'none'; return; }
      var idx = slideIdx(sl);
      if (allowed) { ui.style.display = allowed[idx] ? 'flex' : 'none'; return; }  // instant, no image load
      var check = function () { ui.style.display = recolorable(idx, precompute(idx, img, getComputedStyle(img).objectFit)) ? 'flex' : 'none'; };
      if (img.complete && img.naturalWidth) check(); else img.addEventListener('load', check, { once: true });
    }

    // ---- Colour control: preset dots + full-spectrum colour wheel ----
    var nameEl = ui.querySelector('[data-bbp-color-name]');
    var toggle = ui.querySelector('[data-bbp-color-toggle]');
    var wheel = ui.querySelector('[data-bbp-wheel]');
    var handle = ui.querySelector('[data-bbp-wheel-handle]');
    // Premium colour names — reads as precision control, not "RGB LEDs".
    function colorName(hue, white) {
      if (white) return 'Pure White';
      var h = ((hue % 360) + 360) % 360;
      if (h < 15 || h >= 345) return 'Crimson';
      if (h < 40) return 'Sunset Orange';
      if (h < 66) return 'Amber';
      if (h < 90) return 'Lime';
      if (h < 150) return 'Emerald';
      if (h < 175) return 'Aqua';
      if (h < 196) return 'Cyan';
      if (h < 226) return 'Electric Blue';
      if (h < 256) return 'Sapphire';
      if (h < 286) return 'Violet';
      if (h < 316) return 'Purple';
      return 'Magenta';
    }
    function swatchColor() { return state.white ? '#ffffff' : ('hsl(' + state.hue + ' 90% 55%)'); }
    // Place the wheel handle at (hue angle, saturation radius). Angle 0 = top, clockwise (matches the conic ring).
    function positionHandle() {
      if (!handle) return;
      var r = state.white ? 0 : state.sat, rad = (((state.hue % 360) + 360) % 360) * Math.PI / 180;
      handle.style.left = (50 + Math.sin(rad) * r * 40) + '%';
      handle.style.top = (50 - Math.cos(rad) * r * 40) + '%';
    }
    // The colour var lives on the container so the name, wheel centre, handle and recolor all follow it.
    function reflect() {
      ui.style.setProperty('--nt-knob', swatchColor());
      if (nameEl) nameEl.textContent = colorName(state.hue, state.white);
      positionHandle();
      for (var i = 0; i < swatches.length; i++) {
        var sv = swatches[i].getAttribute('data-bbp-swatch');
        var on = state.white ? (sv === 'white') : (sv !== 'white' && Math.abs(parseFloat(sv) - state.hue) < 1 && state.sat > 0.9);
        swatches[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      }
    }
    function setColor(hue, sat, white) {
      state.white = !!white;
      if (!white) { state.hue = ((hue % 360) + 360) % 360; state.sat = clamp01(sat); }
      state.engaged = true;
      reflect();
      refresh();
    }
    // Preset dots
    swatches.forEach(function (b) {
      b.addEventListener('click', function () {
        var sv = b.getAttribute('data-bbp-swatch');
        if (sv === 'white') setColor(0, 1, true); else setColor(parseFloat(sv), 1, false);
      });
    });
    // "View full spectrum" reveals the wheel
    if (toggle) toggle.addEventListener('click', function () {
      var open = ui.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) positionHandle();
    });
    // Colour wheel — angle = hue, distance from centre = vividness (centre = white).
    if (wheel) {
      var wdrag = false;
      function pickFrom(e) {
        var r = wheel.getBoundingClientRect(); if (!r.width) return;
        var dx = e.clientX - (r.left + r.width / 2), dy = e.clientY - (r.top + r.height / 2);
        var dist = Math.sqrt(dx * dx + dy * dy) / (r.width / 2);
        if (dist < 0.14) { setColor(0, 1, true); return; }
        setColor(Math.atan2(dx, -dy) * 180 / Math.PI, clamp01(dist), false);
      }
      wheel.addEventListener('pointerdown', function (e) { wdrag = true; try { wheel.setPointerCapture(e.pointerId); } catch (_) {} pickFrom(e); e.preventDefault(); });
      wheel.addEventListener('pointermove', function (e) { if (wdrag) { pickFrom(e); e.preventDefault(); } });
      wheel.addEventListener('pointerup', function (e) { wdrag = false; try { wheel.releasePointerCapture(e.pointerId); } catch (_) {} });
      wheel.addEventListener('pointercancel', function () { wdrag = false; });
    }

    // Keep the colour as slides move / clone / lazy-load. Because each canvas rides inside its slide,
    // scrolling itself needs no repaint — we only (re)paint on layout changes so new/cloned slides catch up.
    track.addEventListener('transitionend', function (e) { if (e.propertyName === 'transform') { updateControlVisibility(); refresh(); } });
    var mo = new MutationObserver(function () { try { updateControlVisibility(); if (state.engaged) refresh(); } catch (e) {} });
    mo.observe(track, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    window.addEventListener('resize', function () { recCache = {}; outBuf = null; if (state.engaged) refresh(); });

    // While the gallery is being press-and-hold dragged, no transitionend fires, so keep every slide's
    // colour painted each frame — a freshly-revealed slide never flashes the original photo mid-swipe.
    // Cheap: ensureSlide skips canvases already painted with the current colour.
    var dragActive = false, dragRaf = 0;
    function dragTick() { if (!dragActive) { dragRaf = 0; return; } if (state.engaged) refresh(); dragRaf = requestAnimationFrame(dragTick); }
    track.addEventListener('pointerdown', function () { dragActive = true; if (!dragRaf) dragRaf = requestAnimationFrame(dragTick); }, { passive: true });
    window.addEventListener('pointerup', function () { if (state.engaged) refresh(); setTimeout(function () { dragActive = false; }, 700); }, { passive: true });
    window.addEventListener('pointercancel', function () { dragActive = false; }, { passive: true });

    reflect();
    updateControlVisibility();
    window.addEventListener('load', function () { updateControlVisibility(); reflect(); refresh(); });
  }

  ready(function () {
    var uis = document.querySelectorAll('[data-bbp-recolor-ui]');
    for (var i = 0; i < uis.length; i++) { try { build(uis[i]); } catch (e) { /* fail safe */ } }
  });
})();
